import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../components/ui/Badge';
import {
  GeneralList,
  GeneralListSortableTh,
  Select,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import DateFilterDropdown, { DateRange } from '../../components/FiltersPanel/DateFilterDropdown';
import { getDateRangeForPreset } from '../../components/FiltersPanel/dateFilterHelpers';
import {
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  PlusIcon,
  TrashIcon,
  UploadCloudIcon,
} from '../../components/ui/Icons';
import { useTheme } from '../../contexts/ThemeContext';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { deleteDocument } from '../../services/mutations';
import {
  Document,
  DocumentStatus,
  DocumentsListView,
  useDocumentFileTypes,
  useDocuments,
} from './useData';

const statusVariant: Record<DocumentStatus, 'default' | 'info' | 'success' | 'danger' | 'warning'> = {
  DRAFT: 'default',
  IN_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
};

const statusLabel: Record<DocumentStatus, string> = {
  DRAFT: 'Draft',
  IN_APPROVAL: 'In approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const viewTabs: Array<{
  id: DocumentsListView;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'inbox',
    label: 'Inbox',
    icon: <ClockIcon className="h-4 w-4" />,
    emptyTitle: 'Inbox is empty',
    emptyHint: 'Upload an invoice or other file to scan and prepare for approval.',
  },
  {
    id: 'archive',
    label: 'Archive',
    icon: <FolderIcon className="h-4 w-4" />,
    emptyTitle: 'No matching documents',
    emptyHint: 'Approved and rejected documents for the selected period appear here.',
  },
];

const INBOX_STATUS_OPTIONS = (['DRAFT', 'IN_APPROVAL'] as DocumentStatus[]).map((value) => ({
  value,
  label: statusLabel[value],
}));

const ARCHIVE_STATUS_OPTIONS = (['APPROVED', 'REJECTED'] as DocumentStatus[]).map((value) => ({
  value,
  label: statusLabel[value],
}));

function formatCreated(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDocDate(value?: string | null) {
  if (!value) return '';
  const d = new Date(value.length === 10 ? `${value}T00:00:00.000Z` : value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function formatAmount(value?: number | string | null) {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: T[] }).data;
  }
  return [];
}

type DepartmentOpt = { id: number; name?: string; departmentName?: string; code?: string };

function toYmd(date: Date | null | undefined): string | undefined {
  if (!date || Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
}

function applyCurrentMonthPreset(): {
  preset: string;
  from: string | undefined;
  to: string | undefined;
} {
  const range = getDateRangeForPreset('currentMonth');
  return {
    preset: 'currentMonth',
    from: toYmd(range.start),
    to: toYmd(range.end),
  };
}

export const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const confirm = useConfirm();
  const storageKey = getListStateStorageKey('/documents');
  const [persisted, setPersisted] = usePersistedListFilters<{
    view: DocumentsListView;
    search: string;
    status: DocumentStatus | '';
    fileTypeId: number | '';
    departmentId: number | '';
    documentDatePreset: string | null;
    documentDateFrom?: string;
    documentDateTo?: string;
  }>(storageKey);

  const [view, setView] = useState<DocumentsListView>(() => persisted?.view ?? 'inbox');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState(() => persisted?.search ?? '');
  const [search, setSearch] = useState(() => persisted?.search ?? '');
  const [status, setStatus] = useState<DocumentStatus | ''>(() => persisted?.status ?? '');
  const [fileTypeId, setFileTypeId] = useState<number | ''>(() => persisted?.fileTypeId ?? '');
  const [departmentId, setDepartmentId] = useState<number | ''>(() => persisted?.departmentId ?? '');
  const [documentDatePreset, setDocumentDatePreset] = useState<string | null>(
    () => persisted?.documentDatePreset ?? null,
  );
  const [documentDateFrom, setDocumentDateFrom] = useState<string | undefined>(
    () => persisted?.documentDateFrom,
  );
  const [documentDateTo, setDocumentDateTo] = useState<string | undefined>(
    () => persisted?.documentDateTo,
  );

  useEffect(() => {
    setPersisted({
      view,
      search,
      status,
      fileTypeId,
      departmentId,
      documentDatePreset,
      documentDateFrom,
      documentDateTo,
    });
  }, [
    view,
    search,
    status,
    fileTypeId,
    departmentId,
    documentDatePreset,
    documentDateFrom,
    documentDateTo,
    setPersisted,
  ]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, status, fileTypeId, departmentId, documentDateFrom, documentDateTo, view, itemsPerPage]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'documentDate', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'title' || key === 'supplierName' || key === 'fileType' || key === 'status'
          ? 'asc'
          : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const { data: fileTypes = [] } = useDocumentFileTypes();
  const { data: departmentsRaw } = useSWR('/departments?limit=500', fetcher);
  const departments = useMemo(() => asList<DepartmentOpt>(departmentsRaw), [departmentsRaw]);

  const { data, isLoading, total, totalPages, mutate } = useDocuments(page, search, {
    view,
    status: status || undefined,
    fileTypeId: fileTypeId === '' ? undefined : Number(fileTypeId),
    departmentId: departmentId === '' ? undefined : Number(departmentId),
    documentDateFrom,
    documentDateTo,
    limit: itemsPerPage,
    sortKey: sortConfig.key,
    sortDirection: sortConfig.direction,
  });

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const fileTypeOptions = useMemo(
    () =>
      [...fileTypes]
        .filter((t) => t.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
        .map((t) => ({ value: t.id, label: t.name })),
    [fileTypes],
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((d) => ({
        value: d.id,
        label: d.name || d.departmentName || d.code || `Department #${d.id}`,
      })),
    [departments],
  );

  const statusOptions = view === 'inbox' ? INBOX_STATUS_OPTIONS : ARCHIVE_STATUS_OPTIONS;

  const hasFilters = Boolean(
    search.trim() ||
      status ||
      fileTypeId !== '' ||
      departmentId !== '' ||
      (view === 'archive'
        ? documentDatePreset && documentDatePreset !== 'currentMonth'
        : documentDateFrom || documentDateTo),
  );

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatus('');
    setFileTypeId('');
    setDepartmentId('');
    if (view === 'archive') {
      const month = applyCurrentMonthPreset();
      setDocumentDatePreset(month.preset);
      setDocumentDateFrom(month.from);
      setDocumentDateTo(month.to);
    } else {
      setDocumentDatePreset(null);
      setDocumentDateFrom(undefined);
      setDocumentDateTo(undefined);
    }
  };

  const switchView = (next: DocumentsListView) => {
    if (next === view) return;
    setView(next);
    setStatus('');
    if (next === 'archive') {
      const month = applyCurrentMonthPreset();
      setDocumentDatePreset(month.preset);
      setDocumentDateFrom(month.from);
      setDocumentDateTo(month.to);
    } else {
      setDocumentDatePreset(null);
      setDocumentDateFrom(undefined);
      setDocumentDateTo(undefined);
    }
  };

  const onDocumentDateChange = (value: string | null, range?: DateRange) => {
    if (!value || value === 'all' || !range || (!range.start && !range.end)) {
      if (view === 'archive') {
        const month = applyCurrentMonthPreset();
        setDocumentDatePreset(month.preset);
        setDocumentDateFrom(month.from);
        setDocumentDateTo(month.to);
      } else {
        setDocumentDatePreset(null);
        setDocumentDateFrom(undefined);
        setDocumentDateTo(undefined);
      }
      return;
    }
    setDocumentDatePreset(value);
    setDocumentDateFrom(toYmd(range.start));
    setDocumentDateTo(toYmd(range.end));
  };

  const handleDelete = async (e: React.MouseEvent, doc: Document) => {
    e.stopPropagation();
    const message =
      doc.status === 'DRAFT'
        ? `Delete draft “${doc.title}”?`
        : `Delete “${doc.title}” (${statusLabel[doc.status]})? This also removes any linked approval.`;
    const ok = await confirm({
      title: 'Delete document',
      message,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteDocument(doc.id);
      await mutate();
    } catch (err: any) {
      window.alert(err?.response?.data?.message || err?.message || 'Failed to delete document');
    }
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === view) ?? viewTabs[0], [view]);

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label, icon }) => ({ id, label, icon }))}
      activeTab={view}
      onTabChange={switchView}
      sorting={{ sortConfig, onSort: handleSort }}
      tabTrailing={
        <Link
          to="/documents/import"
          state={returnState}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition',
            colors.textMuted,
            'hover:text-slate-700 dark:hover:text-slate-200',
          )}
        >
          <span className="opacity-70">
            <UploadCloudIcon className="h-4 w-4" />
          </span>
          Bulk import
        </Link>
      }
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search documents…',
        title: 'Searches title, summary, filename, supplier, invoice no.',
      }}
      actions={
        <Link
          to="/documents/new"
          state={returnState}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
            colors.primary,
          )}
        >
          <PlusIcon className="h-4 w-4" />
          New document
        </Link>
      }
      filters={
        <>
          <div className="flex flex-wrap gap-3">
            <div className="relative z-20 min-w-[200px] flex-1 basis-[200px] sm:max-w-[260px]">
              <DateFilterDropdown
                label=""
                selectedValue={documentDatePreset}
                placeholder="Document date"
                onFilterChange={onDocumentDateChange}
              />
            </div>
            <div className="min-w-[150px] flex-1 basis-[150px]">
              <Select
                value={fileTypeId}
                options={fileTypeOptions}
                placeholder="File type"
                showClearButton
                onChange={(e) => setFileTypeId(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="min-w-[140px] flex-1 basis-[140px]">
              <Select
                value={status}
                options={statusOptions}
                placeholder="Status"
                showClearButton
                onChange={(e) => setStatus((e.target.value || '') as DocumentStatus | '')}
              />
            </div>
            <div className="min-w-[150px] flex-1 basis-[150px]">
              <Select
                value={departmentId}
                options={departmentOptions}
                placeholder="Department"
                showClearButton
                onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>
        </>
      }
      resultSummary={
        !isLoading ? (
          <p className={cn('text-xs', colors.textMuted)}>
            {total === 1 ? '1 document' : `${total} documents`}
            {view === 'archive' && documentDatePreset === 'currentMonth' && !hasFilters
              ? ' this month'
              : ''}
            {hasFilters ? ' matching filters' : ''}
            {search.trim() && !hasFilters ? ` matching “${search.trim()}”` : ''}
          </p>
        ) : (
          <span />
        )
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading documents…"
      isEmpty={data.length === 0}
      emptyState={{
        icon:
          view === 'inbox' && !hasFilters ? (
            <DocumentTextIcon className="h-12 w-12" />
          ) : (
            <FolderIcon className="h-12 w-12" />
          ),
        title: hasFilters ? 'No matching documents' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action:
          hasFilters || view === 'archive' ? (
            <button
              type="button"
              onClick={clearFilters}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              Clear filters
            </button>
          ) : (
            <Link
              to="/documents/new"
              state={returnState}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
                colors.primary,
              )}
            >
              <PlusIcon className="h-4 w-4" />
              New document
            </Link>
          ),
      }}
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className="w-[5.5rem] px-3 py-3 font-medium" />
            <GeneralListSortableTh sortKey="title">Title</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="supplierName" className="hidden w-36 lg:table-cell">
              Supplier
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="documentDate" className="hidden w-28 md:table-cell">
              Doc date
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="totalAmount" className="hidden w-24 xl:table-cell" align="right">
              Amount
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="fileType" className="hidden w-28 sm:table-cell">
              File type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="w-36">
              Status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="createdAt" className="hidden w-28 lg:table-cell">
              Created
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {data.map((doc) => (
            <tr
              key={doc.id}
              onClick={() => navigate(`/documents/${doc.id}`, { state: returnState })}
              className={cn(
                'group cursor-pointer border-b transition last:border-b-0',
                colors.border,
                colors.interactiveHover,
              )}
            >
              <td className="px-3 py-3.5">
                <div className="flex items-center flex-nowrap">
                  <Link
                    onClick={(e) => e.stopPropagation()}
                    to={`/documents/${doc.id}`}
                    state={returnState}
                    title="View"
                    aria-label="View document"
                    className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                  >
                    <EyeIcon className="w-5 h-5" />
                  </Link>
                  <button
                    type="button"
                    title="Delete"
                    aria-label="Delete document"
                    onClick={(e) => handleDelete(e, doc)}
                    className={cn(
                      'rounded-lg p-1 transition-colors',
                      isDark ? 'text-red-400' : 'text-red-600',
                      colors.interactiveHover,
                    )}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </td>
              <td className="max-w-0 px-5 py-3.5">
                <div className="min-w-0">
                  <p className={cn('truncate font-medium', colors.text)}>{doc.title}</p>
                  {(doc.invoiceNumber || doc.summary) && (
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                      {doc.invoiceNumber ? `Inv ${doc.invoiceNumber}` : doc.summary}
                    </p>
                  )}
                  <p className={cn('mt-1 truncate text-xs lg:hidden', colors.textMuted)}>
                    {[doc.supplierName, formatDocDate(doc.documentDate)].filter((v) => Boolean(v)).join(' · ') ||
                      formatCreated(doc.createdAt)}
                  </p>
                </div>
              </td>
              <td className={cn('hidden truncate px-3 py-3.5 lg:table-cell', colors.textMuted)}>
                {doc.supplierName || ''}
              </td>
              <td className={cn('hidden whitespace-nowrap px-3 py-3.5 md:table-cell', colors.textMuted)}>
                {formatDocDate(doc.documentDate)}
              </td>
              <td className={cn('hidden whitespace-nowrap px-3 py-3.5 text-right xl:table-cell', colors.textMuted)}>
                {formatAmount(doc.totalAmount)}
              </td>
              <td className={cn('hidden truncate px-3 py-3.5 sm:table-cell', colors.text)}>
                {doc.fileType?.name ?? ''}
              </td>
              <td className="whitespace-nowrap px-3 py-3.5">
                <Badge variant={statusVariant[doc.status]} className="whitespace-nowrap">
                  {statusLabel[doc.status]}
                </Badge>
              </td>
              <td className={cn('hidden whitespace-nowrap px-3 py-3.5 lg:table-cell', colors.textMuted)}>
                {formatCreated(doc.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GeneralList>
  );
};
